'use client'

// Editor surface seam (25_SHARED_DESKUI_SKELETON.md §5, editor follow-up from
// §10.3): the skeleton editor field components read their variant identity and
// every visual utility class from this context. Each surface mounts one
// provider (StdRoutedPage / MailboxMsgPage) supplying its skin; the skeleton
// stays CSS-free per §4.6 and never imports a surface module.

import { createContext, useContext, type ReactNode } from 'react'
import type { RichEditPolicy, RichEditSkin, RichEditVariant } from '@/app/richEdit'
import type { ProductSurfacePath } from './ProductDisplayCatalogContext'

export type EditorVariant = 'std' | 'mailbox'

/**
 * Visual tokens for the shared editor section modal (Configure / Attachments).
 * Optional per surface: mailbox keeps its own dialog and does not supply it.
 */
export interface EditorModalSkin {
  overlayOpen: string
  overlayClosed: string
  dialog: string
  header: string
  headerIcon: string
  title: string
  closeButton: string
  body: string
  nav: string
  navItem: string
  navItemActive: string
  navItemInactive: string
  navIcon: string
  content: string
}

/**
 * Visual tokens for the shared attachment surfaces: the inline thumbnail strip
 * in the editor body and the management grid inside the Attachments modal.
 */
export interface EditorAttachmentsUiSkin {
  strip: string
  stripThumb: string
  stripImg: string
  stripRemove: string
  grid: string
  tile: string
  tileInactive: string
  tileError: string
  preview: string
  name: string
  status: string
  remove: string
  restore: string
  uploadTile: string
  uploadPreview: string
  uploadLabel: string
  empty: string
}

/**
 * Visual class tokens for the skeleton editor fields. Full Tailwind literals
 * only (never composed dynamically — the content scan must see them). The
 * skeleton concatenates contract classes + tokens in a stable order, so a
 * skin that replicates the pre-extraction strings byte-reproduces the DOM
 * class attribute.
 */
export interface EditorSkin {
  /** editor.headShort hidden compat block */
  headShort: string
  head: {
    root: string
    nameKeyRow: string
    /** mailbox-only "From" label; unused (never rendered) on std */
    fromLabel: string
    recipientsRow: string
    subjectRow: string
    subjectLabel: string
    subjectInputBox: string
    subjectInput: string
    /** hidden subject compat button/span */
    compatControl: string
  }
  nameKey: {
    block: string
    /** mailbox-only "More sender options" fold button */
    moreButton: string
    advancedOpen: string
    advancedClosed: string
    /** std compact/extended switch row + control (optional; std modern look) */
    switchRow?: string
    switchButton?: string
  }
  smartName: {
    root: string
    sign: string
    signStatus: { busy: string; ok: string; wrong: string }
    inputBox: string
    inputBoxWrong: string
    input: string
    actions: string
    check: string
    quick: string
    quickItems: string
    quickItem: string
  }
  extName: {
    root: string
    quickRow: string
    label: string
    quickBtn: string
    subKeyBtn: string
    box: string
    textarea: string
    mirror: string
    checkRow: string
    subKeyText: string
    check: string
    emailInfo: string
    emailEdit: string
    code: string
    codeInput: string
    codeBtn: string
    codeInfo: string
  }
  recipients: {
    root: string
    label: string
    field: string
    inputWrap: string
    input: string
    /** Optional error look for a refused commit (invalid/duplicate/not-found). */
    inputErr?: string
    /** Optional look for the short inline error hint next to the input. */
    inputErrHint?: string
    list: string
    token: string
    tokenKind: { email: string; msg: string; self: string }
    tokenText: string
    tokenTextSelf: string
    tokenX: string
    tokenXSelf: string
    /** Optional look for a non-editable token (legacy non_editable rule). */
    tokenLocked?: string
    compat: string
  }
  settings: {
    root: string
    tabs: string
    tab: string
    tabActive: string
    tabInactive: string
    body: string
    paneHidden: string
    panel: string
    legend: string
    row: string
    check: string
    rowCheck: string
    field: string
    combo: string
    textInput: string
    timeRow: string
    timeInput: string
    avatarButton: string
    avatarImage: string
    avatarInput: string
    protectForm: string
  }
  body: {
    root: string
    tools: string
    toolButton: string
    /** highlighted state of the rich/plain toggle button while in rich mode */
    toolButtonActive: string
    /** mailbox '!hidden' override for the legacy inline toolbars */
    legacyToolbarHidden: string
    /** mailbox-only formatting fold row + its buttons */
    richFoldWrap: string
    richFoldButton: string
    textWrap: string
    textInner: string
    textarea: string
    /** the contenteditable rich-mode body surface */
    richSurface: string
    /** the rich-mode formatting toolbar row */
    richToolbar: string
    /** one rich-mode formatting toolbar button */
    richButton: string
    /** shared unpinned creation warning */
    editPinWarning?: string
  }
  /** shared section modal chrome (std/site/gallery/forum; absent on mailbox) */
  modal?: EditorModalSkin
  /** shared inline strip + modal grid attachment tokens (absent on mailbox) */
  attachmentsUi?: EditorAttachmentsUiSkin
}

export interface EditorSurfaceValue {
  variant: EditorVariant
  skin: EditorSkin
  /** New descendants on this surface must keep this persisted product type. */
  creationProductPath?: ProductSurfacePath
  /** The surface owns a mandatory membership gate; shared Configure must not
   * offer a control that suggests the rule can be disabled. */
  registeredUsersRule?: 'optional' | 'forced-hidden'
  /** Optional product-owned restriction for the native attachment picker. */
  attachmentAccept?: string
  /** Allow one picker action to enqueue more than one attachment. */
  attachmentMultiple?: boolean
  richEditVariant: RichEditVariant
  richEditPolicy?: RichEditPolicy
  richEditSkin: RichEditSkin
  richEditDefaultFormat: 'model' | 'html'
}

const EditorSurfaceContext = createContext<EditorSurfaceValue | null>(null)

export function EditorSurfaceProvider({ value, children }: { value: EditorSurfaceValue; children: ReactNode }) {
  return <EditorSurfaceContext.Provider value={value}>{children}</EditorSurfaceContext.Provider>
}

export function useEditorSurface(): EditorSurfaceValue {
  const value = useContext(EditorSurfaceContext)
  if (!value) {
    throw new Error('EditorSurface seam missing: mount the surface EditorSurfaceProvider around the editor (StdRoutedPage / MailboxMsgPage)')
  }
  return value
}
